/**
 * MonthlyMemberAcquisitionChart — live new-member acquisition chart
 *
 * Accepts a ChartSeries prop. If no data, renders empty state.
 */
import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartSeries } from '../../../types/insightsTypes';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface MonthlyMemberAcquisitionChartProps {
  series?: ChartSeries;
}

const MonthlyMemberAcquisitionChart: React.FC<MonthlyMemberAcquisitionChartProps> = ({ series }) => {
  if (!series || series.data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-slate-400 text-sm">
        No acquisition data available
      </div>
    );
  }

  const formatLabel = (period: string): string => {
    try {
      const d = new Date(period);
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    } catch {
      return period;
    }
  };

  const data = {
    labels: series.data.map((d) => formatLabel(d.period)),
    datasets: [
      {
        label: series.label || 'New Members',
        data: series.data.map((d) => d.value),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.3)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#8b5cf6',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function (context: any) {
            return `New Members: ${context.parsed.y}`;
          },
        },
      },
    },
    interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { size: 11 } },
      },
      y: {
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: { color: '#94a3b8', font: { size: 11 } },
      },
    },
  };

  return (
    <div className="h-80">
      <Line data={data} options={options} />
    </div>
  );
};

export default MonthlyMemberAcquisitionChart;